import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { RotateCcw, ArrowLeft, Package } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DebitNoteDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    api.debitNoteGet(token, id)
      .then(r => setData(r))
      .catch(e => { if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error'); })
      .finally(() => setLoading(false));
  }, [token, id, toast]);

  const dn = data?.debit_note || data;
  const journal = data?.journal;
  const sym = dn?.currency_symbol_snapshot || '₹';

  const fmtMoney = (v) => {
    if (v == null) return '—';
    return `${sym}${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="debit-note-detail-page">
        <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!dn) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="debit-note-detail-page">
        <GlassCard className="p-12 text-center">
          <p className="text-gray-500">Debit note not found.</p>
          <Link to="/notes/debit" className="text-brand-purple text-sm font-bold hover:underline mt-2 inline-block">Back to debit notes</Link>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="debit-note-detail-page">
      <Link to="/notes/debit" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-semibold hover:text-gray-700 mb-4" data-testid="dn-back">
        <ArrowLeft size={14} /> Back to Debit Notes
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center">
            <RotateCcw size={24} className="text-violet-600" style={{ transform: 'scaleX(-1)' }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-gray-900 font-extrabold text-xl">{dn.note_no}</h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-violet-50 text-violet-600 border border-violet-200">Debit Note</span>
            </div>
            <p className="text-gray-500 text-sm mt-0.5">{fmtDate(dn.date)} · {dn.supplier_name}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-violet-700 font-extrabold text-2xl tabular-nums" data-testid="dn-total">{fmtMoney(dn.total_returned)}</p>
          <p className="text-gray-400 text-xs mt-0.5">{dn.tax_label_snapshot || 'Tax'}: {fmtMoney(dn.tax_returned)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {dn.po_id && (
            <GlassCard className="p-4">
              <p className="text-gray-400 text-xs font-bold uppercase mb-1">Linked PO</p>
              <p className="text-gray-700 text-sm font-semibold">{dn.po_id.slice(0, 12)}...</p>
            </GlassCard>
          )}

          <GlassCard className="overflow-hidden" data-testid="dn-lines-card">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Returned Items</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Product</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Qty</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Price</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">{dn.tax_label_snapshot || 'Tax'}</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {(dn.line_items || []).map((l, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-5 py-2.5 text-gray-900 font-semibold">{l.product_name}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.qty_returned}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{fmtMoney(l.unit_price)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-500">{fmtMoney(l.tax_amount)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-900 font-bold">{fmtMoney(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={4} className="px-5 py-2.5 text-gray-900 font-extrabold text-xs uppercase text-right">Total Returned</td>
                  <td className="px-5 py-2.5 text-right text-violet-700 font-extrabold tabular-nums">{fmtMoney(dn.total_returned)}</td>
                </tr>
              </tfoot>
            </table>
          </GlassCard>

          {journal && (
            <GlassCard className="overflow-hidden" data-testid="dn-journal-card">
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Journal Entry</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Account</th>
                    <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Type</th>
                    <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Debit</th>
                    <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {(journal.lines || []).map((l, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-5 py-2.5 text-gray-900 font-semibold">{l.account_name}</td>
                      <td className="px-5 py-2.5"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">{l.account_type}</span></td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.debit_rupees > 0 ? fmtMoney(l.debit_rupees) : '—'}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.credit_rupees > 0 ? fmtMoney(l.credit_rupees) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td colSpan={2} className="px-5 py-2.5 text-gray-900 font-extrabold text-xs uppercase">Totals</td>
                    <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{fmtMoney(journal.debit_total)}</td>
                    <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{fmtMoney(journal.credit_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </GlassCard>
          )}
        </div>

        <div className="space-y-4">
          <GlassCard className="p-5">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Details</h3>
            <div className="space-y-2 text-sm">
              {dn.reason && <div><p className="text-gray-400 text-xs font-bold uppercase">Reason</p><p className="text-gray-900 font-semibold mt-0.5">{dn.reason}</p></div>}
              {dn.notes && <div><p className="text-gray-400 text-xs font-bold uppercase">Notes</p><p className="text-gray-700 mt-0.5">{dn.notes}</p></div>}
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700">{fmtDate(dn.created_at)}</span></div>
            </div>
          </GlassCard>

          {dn.stock_movements && dn.stock_movements.length > 0 && (
            <GlassCard className="p-5" data-testid="dn-stock-movements">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Stock Movements</h3>
              <div className="space-y-2">
                {dn.stock_movements.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-gray-400" />
                      <span className="text-gray-700">{m.product_name}</span>
                    </div>
                    <span className={`font-bold tabular-nums ${m.delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {m.delta > 0 ? '+' : ''}{m.delta}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
}
